/**
 * Variables inside a proposal's text: {{client}}, {{contact_first_name}},
 * {{company}}, {{package}}, {{total}}, {{weeks}}, {{valid_until}},
 * {{owner_name}}.
 *
 * Written into any text section in the studio and resolved when the proposal
 * is drawn, on the printed document and on the public page alike, so a
 * renamed client or a changed price never leaves an old value in a letter.
 *
 * A variable with no value is left as written ({{company}}), never blanked:
 * a blank reads as finished, a visible token does not. The studio lists them
 * as blockers before sending (`unresolvedVars`).
 */

const TOKEN = /\{\{\s*([a-zA-Z][\w]*)\s*\}\}/g

export const PROPOSAL_VARIABLES = [
  { key: 'client', label: 'Client (company, else name)' },
  { key: 'contact_first_name', label: 'Contact first name' },
  { key: 'company', label: 'Company' },
  { key: 'package', label: 'Package name' },
  { key: 'total', label: 'Total' },
  { key: 'weeks', label: 'Weeks' },
  { key: 'valid_until', label: 'Valid until' },
  { key: 'owner_name', label: 'Your name' },
]

const text = (v) => String(v ?? '').trim()
const HONORIFIC = /^(dr|mr|mrs|ms|mx|prof|rev)\.?$/i

/** "Ana" from "Ana Alvarez"; "Dr. Alvarez" from "Dr. Ana Alvarez". */
export function contactFirstName(name) {
  const w = text(name).split(/\s+/).filter(Boolean)
  if (!w.length) return ''
  if (HONORIFIC.test(w[0])) return w.length > 1 ? `${w[0]} ${w[w.length - 1]}` : w[0]
  return w[0]
}

const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`

const longDate = (d) => {
  if (!d) return ''
  const s = String(d)
  const [y, m, day] = s.slice(0, 10).split('-').map(Number)
  const date = y && m && day ? new Date(y, m - 1, day) : new Date(s)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

/**
 * The values, from what the proposal knows. Every input is optional; a value
 * that cannot be known is left out, so its token stays visible.
 */
export function proposalVars({ company = '', contact = '', packageName = '', total = 0, weeks = 0, validUntil = null, createdAt = null, validityDays = 30, ownerName = '', now = new Date() } = {}) {
  const out = {}
  const put = (k, v) => { if (text(v)) out[k] = text(v) }
  put('company', company)
  put('client', company || contact)
  put('contact_first_name', contactFirstName(contact))
  put('package', packageName)
  if (Number(total) > 0) put('total', money(total))
  if (Number(weeks) > 0) put('weeks', String(weeks))
  let until = validUntil
  if (!until) {
    const base = createdAt ? new Date(createdAt) : now
    if (!Number.isNaN(base.getTime())) {
      until = new Date(base.getFullYear(), base.getMonth(), base.getDate() + (Number(validityDays) || 30))
    }
  }
  put('valid_until', until instanceof Date
    ? until.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : longDate(until))
  put('owner_name', ownerName || 'Efren Cavazos')
  return out
}

/** Text with its variables filled; unknown or empty ones are left as written. */
export function resolveVars(value, vars = {}) {
  if (typeof value !== 'string' || !value.includes('{{')) return value
  TOKEN.lastIndex = 0
  return value.replace(TOKEN, (whole, key) => (text(vars[key]) ? vars[key] : whole))
}

/** Every string inside a plain object or array, resolved. */
export function resolveDeep(value, vars = {}) {
  if (typeof value === 'string') return resolveVars(value, vars)
  if (Array.isArray(value)) return value.map(v => resolveDeep(v, vars))
  if (value && typeof value === 'object') {
    const out = {}
    for (const [k, v] of Object.entries(value)) out[k] = resolveDeep(v, vars)
    return out
  }
  return value
}

/** Tokens in the document's text that nothing fills, in order, once each. */
export function unresolvedVars(value, vars = {}) {
  const seen = []
  const walk = (v) => {
    if (typeof v === 'string') {
      TOKEN.lastIndex = 0
      let m
      while ((m = TOKEN.exec(v)) !== null) {
        if (!text(vars[m[1]]) && !seen.includes(m[1])) seen.push(m[1])
      }
    } else if (Array.isArray(v)) v.forEach(walk)
    else if (v && typeof v === 'object') Object.values(v).forEach(walk)
  }
  walk(value)
  return seen
}
